import type { ParagraphProcessSteps as ParagraphProcessStepsData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphProcessSteps.module.css";

/**
 * Layout 32 - `PARAGRAPH_PROCESS_STEPS_LAYOUT`: proces 4 kroki ("Jak pracujemy").
 *
 * Struktura:
 *   - Eyebrow (text_1) - maly label uppercase (kolor accent)
 *   - Title h2 (name) - duzy tytul sekcji
 *   - Lead (text) - paragraph pod tytulem
 *   - Grid 4 kart krokow:
 *       - Numer (01-04) generowany CSS-owo (counter-reset/increment na .grid)
 *       - Nazwa kroku (h3)
 *       - Opis kroku (p)
 *       - Opcjonalny badge w prawym gornym rogu (etykieta + tooltip hover-triggered)
 */
export function ParagraphProcessSteps({
  paragraph,
}: {
  paragraph: ParagraphProcessStepsData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: lead, steps } = paragraph;

  const hasSteps = Array.isArray(steps) && steps.length > 0;

  return (
    <section className={styles.section}>
      {eyebrow && eyebrow.trim() !== "" ? (
        <p className={styles.eyebrow}>{eyebrow}</p>
      ) : null}

      {title ? <h2 className={styles.title}>{title}</h2> : null}

      {lead && lead.trim() !== "" ? (
        <p className={styles.lead}>{lead}</p>
      ) : null}

      {hasSteps ? (
        <ol className={styles.grid}>
          {steps.map((step, idx) => {
            const hasBadge = step.badge_label && step.badge_label.trim() !== "";
            const hasTooltip = hasBadge && (
              (step.tooltip_title && step.tooltip_title.trim() !== "") ||
              (step.tooltip_desc && step.tooltip_desc.trim() !== "")
            );
            return (
              <li key={idx} className={styles.step}>
                <div className={styles.num} aria-hidden="true" />
                {hasBadge ? (
                  <div className={styles.badge}>
                    <span className={styles.badgeLabel}>{step.badge_label}</span>
                    {hasTooltip ? (
                      <div className={styles.tooltip} role="tooltip">
                        {step.tooltip_title ? (
                          <strong className={styles.tooltipTitle}>{step.tooltip_title}</strong>
                        ) : null}
                        {step.tooltip_desc ? (
                          <p className={styles.tooltipDesc}>{step.tooltip_desc}</p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {step.name ? <h3 className={styles.stepName}>{step.name}</h3> : null}
                {step.desc ? <p className={styles.stepDesc}>{step.desc}</p> : null}
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );
}
